import { createContext, memo, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router";
import { useIntl } from "react-intl";
import Markdown, { type Components, defaultUrlTransform } from "react-markdown";
import { baseComponents, MARKDOWN_LINK_CLASS } from "@/components/common/markdown-base";
import remarkGfm from "remark-gfm";
import { remarkHtmlBreaks } from "@/lib/remark-html-breaks";

// `remarkHtmlBreaks` after GFM: a `<br>` the model put in a table cell becomes a line break
// instead of the literal text "<br>". Raw HTML stays off; see the plugin for why.
const REMARK_PLUGINS = [remarkGfm, remarkHtmlBreaks];
import { PERSON_HREF, personMentions } from "@/lib/person-mentions";
import { COMPANY_HREF, companyMentions } from "@/lib/company-mentions";
import { useCompanyLogos } from "@/lib/use-company-logos";
import type { CompanyLogoValue } from "@/pages/company/company-logo";
import { CompanyResultRow } from "@/components/common/CompanyResultRow";
import { PersonChoiceCards } from "./PersonChoiceCards";

// The agent links each person it names as `[Full Name](person:<uuid>)` (@adr SPARK-0014-design-primitives-single-source@1).
// We render the name as plain text followed by an info icon that triggers the
// deterministic profile load — never as a navigable anchor. A candidate of a question ("which person do you
// mean?") carries `?choose`, with or without an id, and is offered as a person card below the reply
// (PersonChoiceCards; the grammar lives in lib/person-mentions).
//
// Companies are linked the same way, `[Name](company:<uuid>)`, and render differently: a company on
// a LINE OF ITS OWN becomes a row (logo, name, reason) that opens the company page, while one named
// inside a sentence stays plain text. The model's wording varies between runs, so both shapes of
// "a line of its own" are recognised — a list item and a bare paragraph (@adr SPARK-0050-a-company-link-in-an-answer-is-a-row@1).

// react-markdown's default url sanitizer drops unknown protocols, which would
// strip our `person:` and `company:` schemes; let them through, sanitize everything else normally.
const urlTransform = (url: string) =>
  url.startsWith("person:") || url.startsWith("company:") ? url : defaultUrlTransform(url);

/**
 * Every company a block names as its own entry, in order, or null if it names none that way.
 *
 * Reads the parsed document rather than the text, because "an entry of its own" is a structural
 * fact. It SPLITS AT EVERY COMPANY LINK rather than looking only at the first one: consecutive
 * lines are a single paragraph to markdown (`A\nB` is one block with a newline in the middle), so
 * a first-link-only rule turned the reply the prompt actually asks for into one row with the
 * second company flattened into its subtitle. Splitting also makes two companies on one line two
 * rows, which is the same mistake in a different shape.
 *
 * Null when anything precedes the first company link — that is a company named mid-sentence, and
 * it stays a name. Null, too, for a table cell: the cell is not an entry, and a row inside one
 * would orphan the other columns.
 */
interface HastNode {
  type: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
}

export interface CompanyEntry {
  id: string;
  name: string;
  rest: string;
}

const significant = (nodes: HastNode[]) =>
  nodes.filter((child) => !(child.type === "text" && !(child.value ?? "").trim()));

const hastText = (nodes: HastNode[]): string =>
  nodes.map((n) => (n.type === "text" ? n.value ?? "" : hastText(n.children ?? []))).join("");

/** The company id this node links to, seeing through the emphasis a model likes to put on a name. */
function companyLinkIn(node: HastNode | undefined): { id: string; name: string } | null {
  if (!node) return null;
  if (node.tagName === "strong" || node.tagName === "em") {
    const inner = significant(node.children ?? []);
    return inner.length === 1 ? companyLinkIn(inner[0]) : null;
  }
  if (node.tagName !== "a") return null;
  const id = COMPANY_HREF.exec(String(node.properties?.href ?? ""))?.[1];
  return id ? { id, name: hastText(node.children ?? []).trim() } : null;
}

/** Whether a list is nothing but company rows, and so wants none of a list's own spacing. */
function isAllCompanyRows(node: unknown): boolean {
  const items = ((node as HastNode | undefined)?.children ?? []).filter((child) => child.tagName === "li");
  return items.length > 0 && items.every((item) => companyEntriesIn(item) !== null);
}

/**
 * What introduces a REASON after a company's name — a dash, a colon, a pipe, a comma.
 *
 * Nothing here matches a sentence continuing into its verb, which is the whole point: with no
 * separator the line is prose that happens to open with a linked name, and prose is not a row.
 */
const SEPARATOR = /^[\s]*[\u2014\u2013:,.|-]+[\s]*/;
const SEPARATED = /^[\s]*[\u2014\u2013:,.|-]/;

/** Assigns an introduced trailing text to the company it follows; anything else is dropped. */
function reasonOf(trailing: HastNode[], entries: CompanyEntry[]): void {
  if (trailing.length === 0 || entries.length === 0) return;
  const raw = hastText(trailing);
  if (!SEPARATED.test(raw)) return;
  entries[entries.length - 1]!.rest = raw.replace(SEPARATOR, "").trim();
}

function companyEntriesIn(node: unknown): CompanyEntry[] | null {
  const root = node as HastNode | undefined;
  if (!root?.children) return null;
  let children = significant(root.children);
  // A loose list item wraps its content in a paragraph; a tight one does not.
  if (children.length === 1 && children[0]?.tagName === "p") {
    children = significant(children[0]?.children ?? []);
  }

  const entries: CompanyEntry[] = [];
  let trailing: HastNode[] = [];
  for (const child of children) {
    const link = companyLinkIn(child);
    if (link) {
      // Text standing between two links is one of two things, and the same signal tells them apart:
      // introduced, it is the PREVIOUS company's reason and a soft line break follows it
      // ("[A] — Photovoltaik\n[B] — Fernwärme" is one paragraph); not introduced, it is a
      // conjunction joining two names ("[A] und [B]") and belongs to neither.
      reasonOf(trailing, entries);
      trailing = [];
      entries.push({ ...link, rest: "" });
      continue;
    }
    // Before the first company link, so the block is prose that happens to name one.
    if (entries.length === 0) return null;
    trailing.push(child);
  }
  // What trails the LAST link has nowhere else to belong: introduced, it is that company's reason;
  // otherwise the block is a sentence and not a list of companies at all.
  if (trailing.length > 0) {
    const raw = hastText(trailing);
    // THE SEPARATOR IS THE SIGNAL, and it used to be stripped before anything could read it.
    //
    // A reason is INTRODUCED: "— Kinder- und Jugendhilfe". A sentence just continues: "ist ein
    // Immobilien- und Facility-Dienstleister mit 43.000 Mitarbeitenden." The model writes both, and
    // the row shape only suits the first — laid out as a row, the second is a sentence torn in half,
    // its subject on the left and its predicate in the label column (observed 2026-10-16).
    //
    // Length cannot tell them apart: measured over the live vocabulary, labels average 22 characters
    // and run to 92 (p95 42, p99 55), so every threshold cuts through both.
    if (!SEPARATED.test(raw)) return null;
    // The row has its own typography for that relationship and does not want the dash carried in.
    entries[entries.length - 1]!.rest = raw.replace(SEPARATOR, "").trim();
  }
  return entries.length > 0 ? entries : null;
}

// Click-to-zoom lightbox for reference-corpus figures (@adr SPARK-0033-power-weights-word-scale-plus-exact-field@1). Opens fit-to-window so the whole
// figure is visible; clicking the image toggles to native 100% inside a scrollable container, so the
// fine detail (high-DPI maps) is readable. Native <dialog> gives Esc + focus-trap + backdrop for free.
// Remounted per open (conditional render), so `zoomed` resets each time.
function Lightbox({
  src,
  alt,
  closeLabel,
  onClose,
}: {
  src: string;
  alt: string;
  closeLabel: string;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (el && !el.open) el.showModal();
  }, []);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handler = () => onClose(); // fires on Esc and programmatic close
    el.addEventListener("close", handler);
    // The native modal already closes itself on Esc; stop that keydown from bubbling to window so a
    // global Esc handler (the AssistantDrawer closes the chat on Escape) doesn't ALSO fire on the
    // same keypress. Propagation only — never preventDefault, or the dialog's own Esc-close is lost.
    const stopEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") e.stopPropagation();
    };
    el.addEventListener("keydown", stopEsc);
    return () => {
      el.removeEventListener("close", handler);
      el.removeEventListener("keydown", stopEsc);
    };
  }, [onClose]);

  // Portaled to <body>: the drawer uses `transform` (translate-x for the slide-in), which would
  // otherwise become the containing block for this fixed/top-layer dialog and collapse it to the
  // drawer's width. As a direct child of <body> it sizes to the viewport.
  return createPortal(
    <dialog
      ref={ref}
      onClick={onClose} // click anywhere (dimmed area) closes; the image stops propagation
      className="fixed inset-0 z-[1000] m-0 h-[100dvh] w-[100vw] max-h-none max-w-none border-0 bg-transparent p-0 backdrop:bg-ink/80"
    >
      {/* `flex` + `m-auto` on the image: centers it when it fits, and lets you pan to every edge
          when it overflows (plain `justify-center` would clip the top-left and make it unscrollable). */}
      <div className={zoomed ? "flex h-full w-full overflow-auto" : "flex h-full w-full items-center justify-center p-4"}>
        <img
          src={src}
          alt={alt}
          onClick={(e) => {
            e.stopPropagation();
            setZoomed((z) => !z);
          }}
          className={
            zoomed
              ? "m-auto h-auto max-w-none cursor-zoom-out"
              : "max-h-[90dvh] max-w-[90vw] object-contain cursor-zoom-in"
          }
        />
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label={closeLabel}
        className="fixed right-3 top-3 z-[1001] grid h-9 w-9 place-items-center rounded-full bg-ink text-smycles-title text-ink transition hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-surface/50"
      >
        ✕
      </button>
    </dialog>,
    document.body,
  );
}

// A trailing UNTERMINATED markdown link — `[text](…` with no closing `)` yet. During streaming the
// agent emits `[Name](person:<uuid>)` token by token; until the `)` lands, react-markdown renders
// the half-typed link as raw text (`[Ivan Izzo](person:01a6…`). We collapse that tail to just its
// visible text so the name shows cleanly and upgrades to the icon once the link closes. `$`-anchored,
// so a completed link earlier in the text is untouched.
const TRAILING_OPEN_LINK = /\[([^\]]*)\]\([^)]*$/;

/**
 * What genesis said about the companies this reply names.
 *
 * A context rather than a prop, because the rows are mounted by react-markdown from inside the
 * parsed document and there is no prop path to them. The default answers "nothing known", which is
 * what a reply with no company mentions gets: no provider is mounted, so NO Apollo hook runs —
 * `PersonChoiceCards` gates its own query the same way, and AgentMarkdown is rendered in places
 * (the studio notebook) whose tests mount no client.
 */
const CompanyIdentitiesContext = createContext<{
  logoOf: (id: string) => CompanyLogoValue | null;
  nameOf: (id: string) => string | undefined;
}>({ logoOf: () => null, nameOf: () => undefined });

function CompanyIdentities({ ids, children }: { ids: string[]; children: ReactNode }) {
  const { logoOf, nameOf } = useCompanyLogos(ids);
  // No memo: `useCompanyLogos` allocates both closures afresh on every render, so a memo keyed on
  // them would never hit and would only claim a stability it does not provide.
  return (
    <CompanyIdentitiesContext.Provider value={{ logoOf, nameOf }}>{children}</CompanyIdentitiesContext.Provider>
  );
}

/**
 * Where a company is a NAME and not a control: inside a quotation, which is someone else's words,
 * and inside a table, which is already a grid of values.
 *
 * A framed, clickable card in either reads as a layout accident, and so does a link in one cell and
 * not the next.
 */
const PlainNamesContext = createContext(false);

function PlainCompanyNames({ children }: { children: ReactNode }) {
  return <PlainNamesContext.Provider value={true}>{children}</PlainNamesContext.Provider>;
}

/**
 * One company line, drawn once its identity is known.
 *
 * genesis answering for the id is what makes the row a link: the assistant's company search reaches
 * further than the reader does, so an unanswered id is one they may not open or one that does not
 * exist, and a row linking there would land them on "does not exist or is not visible to you". It
 * is also what supplies the NAME — the model copied one from a tool result, genesis holds the real
 * one, and preferring genesis's costs nothing because the same answer carries it.
 */
/** The entries of one block, each its own row — unless the block is quoted, where they stay names. */
function CompanyRows({
  companies,
  className,
  fallback,
}: {
  companies: CompanyEntry[];
  className?: string;
  fallback: ReactNode;
}) {
  if (useContext(PlainNamesContext)) return <>{fallback}</>;
  return (
    <div className={className}>
      {companies.map((company) => (
        <CompanyRow key={company.id} company={company} />
      ))}
    </div>
  );
}

function CompanyRow({ company }: { company: CompanyEntry }) {
  const { logoOf, nameOf } = useContext(CompanyIdentitiesContext);
  const known = nameOf(company.id);
  return (
    <CompanyResultRow
      id={company.id}
      name={known ?? company.name}
      sub={company.rest || null}
      logo={logoOf(company.id)}
      openable={known !== undefined}
    />
  );
}

/**
 * A company named INSIDE a sentence — the shape a reply takes whenever it answers in prose rather
 * than listing results.
 *
 * It links on exactly the terms a row does: only once genesis has answered for the id. The
 * assistant's company search reaches further than the reader does, so an unanswered id is one they
 * may not open, and linking there would land them on "does not exist or is not visible to you".
 *
 * Before 2026-10-16 this was always plain text, which cost nothing while an inline mention was
 * incidental. Once a sentence became the normal shape of an answer, the company it was about had no
 * way to be opened at all.
 */
function CompanyMention({ id, children }: { id: string; children: ReactNode }) {
  const { nameOf } = useContext(CompanyIdentitiesContext);
  const plain = useContext(PlainNamesContext);
  if (plain || nameOf(id) === undefined) return <>{children}</>;
  return (
    <Link
      to={`/c/${id}`}
      className={MARKDOWN_LINK_CLASS}
    >
      {children}
    </Link>
  );
}

export const AgentMarkdown = memo(function AgentMarkdown({
  content,
  onPersonInfo,
  addedIds,
  streaming = false,
}: {
  content: string;
  onPersonInfo?: (personId: string) => void;
  // Person ids currently accumulating into a comparison batch — their icons show an "added" state.
  addedIds?: ReadonlySet<string>;
  streaming?: boolean;
}) {
  const intl = useIntl();
  const navigate = useNavigate();
  const infoLabel = intl.formatMessage({
    id: "agent.personInfo.label",
    defaultMessage: "Load full profile",
  });
  const expandLabel = intl.formatMessage({ id: "agent.expandImage", defaultMessage: "View full size" });
  const closeLabel = intl.formatMessage({ id: "agent.closeImage", defaultMessage: "Close image" });

  // The figure currently popped out in the lightbox (null = closed). AgentMarkdown is the only image
  // renderer, so the state lives here — no prop-drilling through AssistantDrawer.
  const [zoom, setZoom] = useState<{ src: string; alt: string } | null>(null);

  const components = useMemo<Components>(
    () => ({
      ...baseComponents,
      // A company named as its own entry is a row; anything else keeps the base rendering. The row
      // replaces the entry's own markup, so the list loses its bullet for that item — the row IS
      // the bullet, and a disc beside a framed card reads as two markers for one thing.
      // A list of nothing but companies drops the SPACE A LIST PUTS BETWEEN ITS ITEMS. The rows are
      // one block of results and read as one; spaced apart, each card reads as a separate thing.
      // The same companies written on consecutive lines already stack flush, and the two shapes are
      // the model's free choice — they must not look different.
      ul: ({ node, children }) => (
        <ul
          className={`mb-2 list-disc pl-5 last:mb-0 marker:text-ink-muted${isAllCompanyRows(node) ? "" : " space-y-0.5"}`}
        >
          {children}
        </ul>
      ),
      ol: ({ node, children }) => (
        <ol
          className={`mb-2 list-decimal pl-5 last:mb-0 marker:text-ink-muted${isAllCompanyRows(node) ? "" : " space-y-0.5"}`}
        >
          {children}
        </ol>
      ),
      li: ({ node, children }) => {
        const companies = companyEntriesIn(node);
        if (!companies) return <li className="pl-0.5">{children}</li>;
        return (
          // FLUSH, not spaced. The rows are one list of companies and read as a block; the gap the
          // list puts between its items made each card look like a separate thing. `space-y-0.5`
          // from the `ul` still separates them by a hairline, which is all the separation the
          // cards' own edges need — the `mb-1` on top of it was the visible gap.
          <li className="-ml-5 list-none">
            <CompanyRows companies={companies} fallback={<span className="pl-0.5">{children}</span>} />
          </li>
        );
      },
      p: ({ node, children }) => {
        const companies = companyEntriesIn(node);
        if (!companies) return <p className="mb-2 last:mb-0">{children}</p>;
        return (
          <CompanyRows
            companies={companies}
            // The same hairline the list leaves between its items, so a reply that writes its
            // companies as paragraphs stacks exactly like one that writes them as a list.
            className="mb-0.5 last:mb-0"
            fallback={<p className="mb-2 last:mb-0">{children}</p>}
          />
        );
      },
      // A quotation is someone else's words shown as such. A framed, clickable card inside the
      // quote rule reads as a layout accident, so a company link quoted stays a name.
      blockquote: ({ children }) => (
        <PlainCompanyNames>
          <blockquote className="mb-2 border-l-2 border-ink/15 pl-2 text-ink last:mb-0">
            {children}
          </blockquote>
        </PlainCompanyNames>
      ),
      // A table is already a grid of values to compare; a link in one cell and not the next reads as
      // a column that means something. This needed no marking until 2026-10-16, because every
      // company link outside a row rendered as bare text.
      table: ({ children }) => (
        <PlainCompanyNames>
          <div className="mb-2 overflow-x-auto last:mb-0">
            <table className="w-full border-collapse text-left text-smycles-meta">{children}</table>
          </div>
        </PlainCompanyNames>
      ),
      // Reference-corpus figures: constrain to the drawer width and open the lightbox on activate.
      // A real <button> wrapper (not a click-only <img>) makes the trigger keyboard- and
      // screen-reader-reachable — Tab to focus, Enter/Space to open, visible :focus-visible ring.
      img: ({ src, alt }) => {
        const url = typeof src === "string" ? src : undefined;
        // Only corpus figures (served same-origin via the /ai/images proxy) are real, zoomable images.
        // Any other src is blocked by CSP `img-src 'self'`, so rendering it as an <img> yields a
        // broken-image icon that's still clickable and opens an empty lightbox. Render those as inert
        // alt-text instead — no broken image, no fake zoom trigger, no cross-origin load attempt.
        if (!url || !url.startsWith("/ai/images/")) {
          return alt ? <span className="text-ink-muted">{alt}</span> : null;
        }
        return (
          <button
            type="button"
            onClick={() => setZoom({ src: url, alt: alt ?? "" })}
            aria-label={expandLabel}
            title={expandLabel}
            className="my-2 inline-block max-w-full cursor-zoom-in rounded-md p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-accent-ui"
          >
            <img src={url} alt={alt ?? ""} loading="lazy" className="block h-auto max-w-full rounded-md" />
          </button>
        );
      },
      a: ({ href, children }) => {
        const person = href ? PERSON_HREF.exec(href) : null;
        const personId = person?.[1];
        if (personId && onPersonInfo) {
          const id = personId;
          const added = addedIds?.has(id) ?? false;
          return (
            <span className="whitespace-nowrap">
              {children}
              <button
                type="button"
                onClick={() => onPersonInfo(id)}
                aria-label={infoLabel}
                title={infoLabel}
                className={`ml-0.5 inline-grid h-4 w-4 translate-y-0.5 place-items-center rounded-full align-baseline transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui ${
                  added ? "bg-accent/15 text-ink-accent" : "text-ink-muted hover:bg-ink/[0.06] hover:text-ink"
                }`}
              >
                <svg viewBox="0 0 16 16" aria-hidden="true" className="h-3.5 w-3.5">
                  <circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.3" />
                  <circle cx="8" cy="4.8" r="0.9" fill="currentColor" />
                  <line x1="8" y1="7.2" x2="8" y2="11.4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                </svg>
              </button>
            </span>
          );
        }
        // A real link (or a person link with no handler) — open externally.
        if (person) return <>{children}</>;
        // A company named inside a sentence: a link to its page once genesis has answered for the
        // id, the bare name otherwise. Matched on the SCHEME and not on a well-formed id —
        // `urlTransform` lets the whole scheme past the sanitiser, so anything this does not catch
        // falls through to the generic branch below and renders as
        // `<a href="company:12" target="_blank">`, a dead anchor that opens a blank tab.
        if (href?.startsWith("company:")) {
          const companyId = COMPANY_HREF.exec(href)?.[1];
          // A malformed or half-streamed id is the name and nothing else: there is no page to open.
          return companyId ? <CompanyMention id={companyId}>{children}</CompanyMention> : <>{children}</>;
        }
        // App-internal links (tour follow-ups, deep links) navigate in place, no new tab.
        // Rendered as accent chips — same look as the tour's section-question chips.
        if (href?.startsWith("/")) {
          return (
            <a
              href={href}
              onClick={(e) => {
                e.preventDefault();
                navigate(href);
              }}
              className="box-flat my-0.5 inline-block rounded-lg border-2 border-accent-decoration px-3 py-1 text-smycles-meta font-semibold text-ink-accent no-underline transition hover:bg-ink/[0.04]"
            >
              {children}
            </a>
          );
        }
        // In-document anchor (a generated table of contents, e.g. the importer
        // guide's kind list). Scrolling is what a reader expects; opening a new
        // tab on "#audience" lands them on a blank page.
        if (href?.startsWith("#")) {
          return (
            <a
              href={href}
              onClick={(e) => {
                e.preventDefault();
                document.getElementById(href.slice(1))?.scrollIntoView({ block: "start" });
              }}
              className="text-ink-accent underline underline-offset-2"
            >
              {children}
            </a>
          );
        }
        // Only a target with a real URL scheme (http:, https:, mailto:…) is a genuine external link.
        // A bare, schemeless, non-`/` target is the model citing a doc as `[name](tool_name)` (spec §7;
        // agent-service#64 "A7" tells it to use plain text) — render the text, not a dead external link.
        if (href && /^[a-z][a-z0-9+.-]*:/i.test(href)) {
          return (
            <a href={href} target="_blank" rel="noreferrer" className="text-ink-accent underline underline-offset-2">
              {children}
            </a>
          );
        }
        return <>{children}</>;
      },
    }),
    [onPersonInfo, infoLabel, addedIds, expandLabel, navigate],
  );

  const display = streaming ? content.replace(TRAILING_OPEN_LINK, "$1") : content;
  // Offered once the reply is complete: a half-streamed question has no settled candidates yet.
  const mentions = useMemo(() => (streaming ? [] : personMentions(content)), [content, streaming]);
  // Same reason, one rung sharper: a half-typed uuid is a company id nobody has, and asking for it
  // would spend a hydration round per token. The PROVIDER is mounted on the text instead (below),
  // which does not change when streaming ends — gating it on this list rebuilt the whole rendered
  // reply at that moment, losing selection and scroll inside the bubble.
  const companyIds = useMemo(
    () => (streaming ? [] : companyMentions(content).map((mention) => mention.id)),
    [content, streaming],
  );

  return (
    <>
      {/* break-words (overflow-wrap, inherited): a long unbreakable token — e.g. a citation source
          filename or URL with no spaces/hyphens to break on — would otherwise overflow the bubble. */}
      <div className="break-words">
        {content.includes("company:") ? (
          <CompanyIdentities ids={companyIds}>
            <Markdown remarkPlugins={REMARK_PLUGINS} components={components} urlTransform={urlTransform}>
              {display}
            </Markdown>
          </CompanyIdentities>
        ) : (
          <Markdown remarkPlugins={REMARK_PLUGINS} components={components} urlTransform={urlTransform}>
            {display}
          </Markdown>
        )}
      </div>
      <PersonChoiceCards mentions={mentions} />
      {zoom && (
        <Lightbox src={zoom.src} alt={zoom.alt} closeLabel={closeLabel} onClose={() => setZoom(null)} />
      )}
    </>
  );
});
